import { useState } from 'react'

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  Accordion,
  ActionIcon,
  Menu,
  Switch,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core'
import { GripVertical } from 'lucide-react'

import { StepType } from 'src/graphql/types/steps'
import IconAdd from 'src/icons/IconAdd'
import IconCheckmark from 'src/icons/IconCheckmark'
import IconClose from 'src/icons/IconClose'
import { formatStepType } from 'src/lib/formatters'

// Available step types
const AVAILABLE_STEP_TYPES = [
  { value: StepType.DOT_APP },
  { value: StepType.MVR },
  { value: StepType.PSP },
  { value: StepType.CDL },
  { value: StepType.CDLIS },
  { value: StepType.BACKGROUND_CHECK },
  { value: StepType.CLEARINGHOUSE },
  { value: StepType.DOT_MEDICAL },
  { value: StepType.VERIFICATION_OF_EMPLOYMENT },
  { value: StepType.DRUG_AND_ALCOHOL_TESTING },
  { value: StepType.ROAD_TEST },
]

export interface StepConfig {
  stepType: string
  customStepId?: string
  label?: string
  enabled: boolean
  order: number
}

// Sortable step item component
const SortableStepItem = ({
  step,
  onToggle,
}: {
  step: StepConfig
  onToggle: (stepType: string, enabled: boolean) => void
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: step.customStepId ?? step.stepType })

  // Application step (DOT_APP) should always be enabled and not editable
  const isApplicationStep = step.stepType === StepType.DOT_APP

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex w-full flex-row items-center justify-between rounded p-1 ${
        isDragging ? 'bg-gray-100' : ''
      }`}
    >
      <div className="flex items-center gap-2">
        <div
          {...attributes}
          {...listeners}
          className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
        >
          <GripVertical size={16} />
        </div>
        <Text className="text-sm text-doubleNickel-gray-800">
          {step.label || formatStepType(step.stepType as any, false)}
        </Text>
      </div>
      <Tooltip
        label="Application step is required and cannot be disabled"
        disabled={!isApplicationStep}
        withArrow
        position="left"
      >
        <div>
          <Switch
            size="sm"
            checked={step.enabled}
            disabled={isApplicationStep}
            onChange={(event) =>
              onToggle(
                step.customStepId ?? step.stepType,
                event.currentTarget.checked
              )
            }
          />
        </div>
      </Tooltip>
    </div>
  )
}

interface ChecklistSectionProps {
  steps: StepConfig[]
  onStepsChange: (steps: StepConfig[]) => void
  activeSection: string | null
  onActiveSectionChange: (section: string | null) => void
  onCreateCustomStep: (label: string) => Promise<boolean>
  creatingCustomStep?: boolean
}

const ChecklistSection = ({
  steps,
  onStepsChange,
  activeSection,
  onActiveSectionChange,
  onCreateCustomStep,
  creatingCustomStep = false,
}: ChecklistSectionProps) => {
  const [isAddingStep, setIsAddingStep] = useState(false)
  const [newStepLabel, setNewStepLabel] = useState('')

  const openDraft = () => {
    if (isAddingStep) return
    setNewStepLabel('')
    setIsAddingStep(true)
  }

  const cancelDraft = () => {
    setIsAddingStep(false)
    setNewStepLabel('')
  }

  const confirmDraft = async () => {
    if (creatingCustomStep) return
    const label = newStepLabel.trim()
    if (!label) return
    const created = await onCreateCustomStep(label)
    if (created) {
      setNewStepLabel('')
      setIsAddingStep(false)
    }
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event

    if (over && active.id !== over.id) {
      const oldIndex = steps.findIndex(
        (item) => (item.customStepId ?? item.stepType) === active.id
      )
      const newIndex = steps.findIndex(
        (item) => (item.customStepId ?? item.stepType) === over.id
      )
      onStepsChange(arrayMove(steps, oldIndex, newIndex))
    }
  }

  const handleToggleStep = (key: string, enabled: boolean) => {
    onStepsChange(
      steps.map((step) =>
        (step.customStepId ?? step.stepType) === key
          ? { ...step, enabled }
          : step
      )
    )
  }

  const enabledCount = steps.filter((s) => s.enabled).length

  return (
    <Accordion
      value={activeSection}
      onChange={onActiveSectionChange}
      styles={{
        root: {
          padding: 0,
        },
        item: {
          border: 'none',
        },
        control: {
          padding: 0,
        },
        content: {
          padding: 0,
        },
      }}
    >
      <Accordion.Item value="checklist">
        <Accordion.Control>
          <div className="flex items-center justify-between">
            <Text size="xs" fw={400} className="text-doubleNickel-gray-500">
              CHECKLIST
            </Text>
            <Text className="mr-2 text-xs text-doubleNickel-gray-500">
              {`${enabledCount} ${enabledCount <= 1 ? 'step' : 'steps'}`}
            </Text>
          </div>
        </Accordion.Control>
        <Accordion.Panel>
          <div className="flex flex-col gap-3 pt-3">
            <div className="flex items-center justify-between">
              <Text className="text-xs text-doubleNickel-gray-600">
                Toggle steps on or off and drag to reorder them for this
                template.
              </Text>
              <Menu shadow="md" position="bottom-end" width={250}>
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className="text-doubleNickel-gray-600"
                  >
                    <IconAdd
                      className="h-4 w-4"
                      stroke="currentColor"
                      fill="none"
                    />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  <Menu.Item
                    className="text-sm text-doubleNickel-gray-900"
                    onClick={openDraft}
                  >
                    Add custom step
                  </Menu.Item>
                </Menu.Dropdown>
              </Menu>
            </div>
            <div className="space-y-1 rounded-lg border border-gray-200 p-3">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={steps.map(
                    (step) => step.customStepId ?? step.stepType
                  )}
                  strategy={verticalListSortingStrategy}
                >
                  {steps.map((step) => (
                    <SortableStepItem
                      key={step.customStepId ?? step.stepType}
                      step={step}
                      onToggle={handleToggleStep}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {isAddingStep && (
                <div className="flex w-full items-center gap-2 p-1">
                  <TextInput
                    className="flex-1"
                    size="xs"
                    placeholder="Custom step name"
                    value={newStepLabel}
                    disabled={creatingCustomStep}
                    onChange={(event) =>
                      setNewStepLabel(event.currentTarget.value)
                    }
                  />
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className="text-doubleNickel-gray-600"
                    onClick={confirmDraft}
                    loading={creatingCustomStep}
                    disabled={!newStepLabel.trim() || creatingCustomStep}
                    aria-label="Save custom step"
                  >
                    <IconCheckmark className="h-4 fill-none stroke-doubleNickel-gray-500" />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    className="text-doubleNickel-gray-600"
                    onClick={cancelDraft}
                    disabled={creatingCustomStep}
                    aria-label="Cancel"
                  >
                    <IconClose className="h-4 fill-none stroke-doubleNickel-gray-500" />
                  </ActionIcon>
                </div>
              )}
            </div>
          </div>
        </Accordion.Panel>
      </Accordion.Item>
    </Accordion>
  )
}

export { AVAILABLE_STEP_TYPES }
export default ChecklistSection
